<template>
  <div class="sv-page" :class="{ embedded: embedded }">

    <!-- Loading overlay -->
    <div v-if="isLoading" class="sv-loading">
      <svg width="28" height="28" viewBox="0 0 20 20" fill="none" class="sv-spin"><circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="2" stroke-dasharray="40" stroke-dashoffset="15" stroke-linecap="round"/></svg>
    </div>

    <!-- Not found -->
    <div v-else-if="!submission" class="sv-empty">
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
      <p>Submissão não encontrada.</p>
      <button class="sv-btn sv-btn--ghost" @click="back">← Voltar</button>
    </div>

    <!-- Print preview replaces submission content inline -->
    <SubmissionPrintPreview
      v-else-if="!isLoading && showPreview"
      :submission="submission"
      @close="showPreview = false"
    />

    <template v-else-if="!isLoading">
      <!-- Header -->
      <div class="sv-header">
        <button class="sv-back" @click="back">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><polyline points="15 18 9 12 15 6"/></svg>
          Voltar
        </button>
        <div class="sv-header-main">
          <h1 class="sv-title">{{ submission.title }}</h1>
          <span
            class="sv-status"
            :class="submission.status === 'enviado' ? 'sv-status--sent' : 'sv-status--draft'"
          >{{ submission.status === 'enviado' ? 'Enviado' : 'Rascunho' }}</span>
        </div>
        <p class="sv-created">Criado em {{ formatDate(submission.createdAt) }}</p>
      </div>

      <!-- Meta card -->
      <div class="sv-card">
        <div class="sv-card-label">Informações</div>
        <div class="sv-meta-grid">
          <div class="sv-meta-item">
            <span class="sv-meta-key">Atração</span>
            <span class="sv-meta-val">{{ submission.attraction || '—' }}</span>
          </div>
          <div class="sv-meta-item">
            <span class="sv-meta-key">Data do relatório</span>
            <span class="sv-meta-val">{{ formatReportDate(submission.reportDate) }}</span>
          </div>
          <div class="sv-meta-item">
            <span class="sv-meta-key">Autor</span>
            <span class="sv-meta-val sv-author">
              <span class="sv-avatar">{{ submission.author.name.charAt(0).toUpperCase() }}</span>
              {{ submission.author.name }}
            </span>
          </div>
          <div class="sv-meta-item" v-if="submission.coAuthors && submission.coAuthors.length">
            <span class="sv-meta-key">Co-autores</span>
            <span class="sv-meta-val">{{ submission.coAuthors.map(resolveCoAuthorName).join(', ') }}</span>
          </div>
          <div class="sv-meta-item" v-if="submission.collaborators && submission.collaborators.length">
            <span class="sv-meta-key">Colaboradores</span>
            <span class="sv-meta-val">{{ submission.collaborators.join(', ') }}</span>
          </div>
          <div class="sv-meta-item sv-meta-item--full" v-if="submission.tools && submission.tools.length">
            <span class="sv-meta-key">Ferramentas</span>
            <div class="sv-tags">
              <template v-for="tool in submission.tools" :key="tool.id">
                <span class="sv-tag" v-if="tool.text">{{ getMachineTitle(tool.text) }}</span>
              </template>
            </div>
          </div>
        </div>
      </div>

      <!-- Tools with images -->
      <div class="sv-card" v-if="submission.tools && submission.tools.some(t => t.images && t.images.length)">
        <div class="sv-card-label">Equipamentos — Fotos</div>
        <div class="sv-activities">
          <div v-for="tool in submission.tools.filter(t => t.images && t.images.length)" :key="`tool-imgs-${tool.id}`" class="sv-activity">
            <div class="sv-activity-title">{{ getMachineTitle(tool.text) || '—' }}</div>
            <div class="sv-img-grid">
              <div class="sv-img-wrap" v-for="(img, idx) in tool.images" :key="`tool-img-${idx}`">
                <img :src="resolveUrl(img)" alt="imagem do equipamento" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Activities -->
      <div class="sv-card" v-if="submission.activities && submission.activities.length">
        <div class="sv-card-label">Atividades</div>
        <div class="sv-activities">
          <div
            class="sv-activity"
            v-for="activity in submission.activities"
            :key="activity.id"
          >
            <div class="sv-activity-title">{{ activity.description || 'Atividade' }}</div>
            <div v-if="activity.images && activity.images.length" class="sv-img-grid">
              <div
                class="sv-img-wrap"
                v-for="(img, index) in activity.images"
                :key="`${activity.id}-img-${index}`"
              >
                <img :src="resolveUrl(img)" alt="imagem da atividade" />
              </div>
            </div>
            <p v-else class="sv-no-img">Sem imagens</p>
          </div>
        </div>
      </div>

      <!-- Observations -->
      <div class="sv-card" v-if="submission.observations && submission.observations.length">
        <div class="sv-card-label">Observações</div>
        <div class="sv-activities">
          <div v-for="obs in submission.observations" :key="obs.id" class="sv-activity">
            <div class="sv-activity-title">{{ obs.text || '—' }}</div>
            <div v-if="obs.images && obs.images.length" class="sv-img-grid">
              <div class="sv-img-wrap" v-for="(img, idx) in obs.images" :key="`obs-img-${idx}`">
                <img :src="resolveUrl(img)" alt="imagem da observação" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="sv-actions">
        <button class="sv-btn sv-btn--ghost" @click="back">← Voltar</button>
        <button class="sv-btn sv-btn--print" @click="showPreview = true">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><rect x="3" y="1" width="10" height="14" rx="1.5"/><path d="M5 4h6M5 7h6M5 10h4" stroke-linecap="round"/></svg>
          Visualizar impressão
        </button>
        <button class="sv-btn sv-btn--primary" @click="edit" v-if="canEdit">Editar</button>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { navigationStore } from '@/store/navigation'
import { submissionsStore } from '@/store/submissions'
import { sessionStore } from '@/store/session'
import { machineryStore } from '@/store/machinery'
import { accountsStore } from '@/store/accounts'
import { formatDateOnly, formatDateTime } from '@/utils/date'
import { resolveUrl } from '@/utils/resolveUrl'
import SubmissionPrintPreview from '@/components/SubmissionPrintPreview.vue'

export default defineComponent({
  name: 'SubmissionView',
  components: { SubmissionPrintPreview },
  props: {
    embedded: {
      type: Boolean,
      default: false
    },
    submissionId: {
      type: String,
      default: ''
    }
  },
  emits: ['back', 'edit'],
  setup(props, { emit }) {
    const route = useRoute()
    const router = useRouter()
    const showPreview = ref(false)
    const isLoading = ref(true)
    const submission = computed(() => {
      const id = props.submissionId || (route.params.id as string)
      return submissionsStore.getById(id)
    })

    const canEdit = computed(() => {
      const user = sessionStore.state.user
      if (!user || !submission.value) return false
      return user.role === 'admin-master' || user.role === 'admin-obra' || user.name === submission.value.author.name
    })

    const back = () => {
      if (props.embedded) {
        emit('back')
      } else {
        navigationStore.goBack(router, '/submissions')
      }
    }
    const edit = () => {
      const item = submission.value
      if (!item) return
      if (props.embedded) {
        emit('edit', item.id)
        return
      }
      if (item.category === 'dialogo') {
        router.push(`/safety/dialogo/${item.id}`)
        return
      }
      if (item.category === 'ocorrido') {
        router.push(`/safety/ocorrido/${item.id}`)
        return
      }
      if (item.category === 'permissao') {
        router.push('/forgot')
        return
      }
      router.push(`/submission/${item.id}/edit`)
    }

    const getMachineTitle = (id: string) => {
      if (!id) return ''
      const m = machineryStore.state.machinery.find(m => m.id === id)
      return m ? m.title : id
    }

    onMounted(async () => {
      try {
        await Promise.all([
          submissionsStore.refresh().catch(() => undefined),
          machineryStore.loadMachinery().catch(() => undefined)
        ])
      } finally {
        isLoading.value = false
      }
    })

    const resolveCoAuthorName = (username: string) => {
      const user = accountsStore.all().find(u => u.username === username)
      return user?.name || username
    }

    return {
      submission,
      showPreview,
      isLoading,
      formatDate: formatDateTime,
      formatReportDate: formatDateOnly,
      back,
      edit,
      canEdit,
      resolveUrl,
      getMachineTitle,
      resolveCoAuthorName
    }
  }
})
</script>

<style scoped lang="scss">
$accent: var(--system-navbar, #0f4c81);
$radius: 14px;
$border: rgba(31, 30, 26, 0.1);

/* ── Page wrapper ─────────────────────────────────── */
.sv-page {
  max-width: 760px;
  margin: 0 auto;
  padding: 28px 20px 48px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.sv-page.embedded {
  padding: 18px 16px 28px;
  max-width: none;
}

@media (max-width: 760px) {
  .sv-page.embedded {
    padding: 14px 12px 22px;
  }
}

/* ── Loading state ────────────────────────────────── */
.sv-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  color: var(--ink-muted, #888);
}

.sv-spin {
  animation: sv-spin 1s linear infinite;
}

@keyframes sv-spin { to { transform: rotate(360deg); } }

/* ── Empty state ──────────────────────────────────── */
.sv-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 64px 20px;
  color: var(--ink-muted, #888);
  text-align: center;

  svg { opacity: .35; }
  p { font-size: 15px; }
}

/* ── Header ───────────────────────────────────────── */
.sv-header {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.sv-back {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  font-size: 13px;
  color: var(--ink-muted, #888);
  cursor: pointer;
  padding: 0;
  width: fit-content;

  &:hover { color: var(--ink, #1a1a1a); }
}

.sv-header-main {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.sv-title {
  font-size: 22px;
  font-weight: 700;
  color: var(--ink, #1a1a1a);
  margin: 0;
}

.sv-status {
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: .5px;

  &--sent {
    background: #d1fae5;
    color: #065f46;
  }
  &--draft {
    background: #f3f4f6;
    color: #6b7280;
  }
}

.sv-created {
  font-size: 12px;
  color: var(--ink-muted, #888);
  margin: 0;
}

/* ── Card ─────────────────────────────────────────── */
.sv-card {
  background: var(--card, #fff);
  border: 1px solid $border;
  border-radius: $radius;
  padding: 20px 24px;
}

.sv-card-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .8px;
  color: var(--ink-muted, #888);
  margin-bottom: 16px;
}

/* ── Meta grid ────────────────────────────────────── */
.sv-meta-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 24px;
}

.sv-meta-item {
  display: flex;
  flex-direction: column;
  gap: 3px;

  &--full {
    grid-column: 1 / -1;
  }
}

.sv-meta-key {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .5px;
  color: var(--ink-muted, #888);
}

.sv-meta-val {
  font-size: 14px;
  color: var(--ink, #1a1a1a);
  font-weight: 500;
}

.sv-author {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sv-avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: $accent;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.sv-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 2px;
}

.sv-tag {
  background: rgba(15, 76, 129, 0.08);
  color: $accent;
  font-size: 12px;
  font-weight: 500;
  padding: 2px 10px;
  border-radius: 20px;
}

/* ── Activities ───────────────────────────────────── */
.sv-activities {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.sv-activity {
  background: var(--bg, #f8f7f4);
  border: 1px solid $border;
  border-radius: 10px;
  padding: 16px;
}

.sv-activity-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--ink, #1a1a1a);
  margin-bottom: 12px;
}

.sv-img-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 8px;
}

.sv-img-wrap {
  border-radius: 8px;
  overflow: hidden;
  aspect-ratio: 4/3;
  background: $border;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform .2s;
  }

  &:hover img { transform: scale(1.04); }
}

.sv-no-img {
  font-size: 13px;
  color: var(--ink-muted, #888);
  margin: 0;
}

/* ── Observations ─────────────────────────────────── */
.sv-obs-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sv-obs-item {
  font-size: 14px;
  color: var(--ink, #1a1a1a);
  padding-left: 16px;
  position: relative;

  &::before {
    content: '•';
    position: absolute;
    left: 0;
    color: $accent;
    font-weight: 700;
  }
}

/* ── Actions ──────────────────────────────────────── */
.sv-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 4px;
}

.sv-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: opacity .15s, background .15s;

  &--ghost {
    background: transparent;
    border: 1.5px solid $border;
    color: var(--ink, #1a1a1a);
    &:hover { background: rgba(31, 30, 26, 0.05); }
  }

  &--print {
    background: transparent;
    border: 1.5px solid $border;
    color: var(--ink, #1a1a1a);
    &:hover { background: rgba(31, 30, 26, 0.05); }
  }

  &--primary {
    background: $accent;
    color: #fff;
    &:hover { opacity: .88; }
  }
}

/* ── Responsive ───────────────────────────────────── */
@media (max-width: 540px) {
  .sv-meta-grid {
    grid-template-columns: 1fr;
  }
  .sv-meta-item--full {
    grid-column: unset;
  }
  .sv-img-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
